import { keyframes } from '@emotion/react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { useBoolean } from 'src/hooks/use-boolean';

import { Iconify } from 'src/components/iconify';
import { ConfirmDialog } from 'src/components/custom-dialog';

import { isUserFolderReport } from './report-tree-utils';
import { ReportContextDialog } from './report-context-dialog';
import { useFavoriteToggle } from '../hooks/use-favorite-toggle';
import { ReportOpenInNewTabButton } from './report-open-new-tab-button';

// ----------------------------------------------------------------------

const starPulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.3);
  }
  100% {
    transform: scale(1);
  }
`;

// ----------------------------------------------------------------------

export function ReportTableRow({
  report,
  favorites,
  onToggleFavorite,
  onReportClick,
  onDeleteReport,
  showActionsColumn = false,
  canEditContext = false,
  busyState,
  compact = false,
}) {
  const router = useRouter();
  const confirm = useBoolean();
  const contextDialog = useBoolean();
  const { loadingFavorites, animatingFavorites, handleToggleFavorite } =
    useFavoriteToggle(onToggleFavorite);

  const handleReportClick = () => {
    if (onReportClick) {
      onReportClick(report.uri, report.resourceType || 'reportUnit');
      return;
    }

    router.push(paths.reports.view(report.uri));
  };

  const handleDelete = async () => {
    const success = await onDeleteReport(report.uri);
    if (success) {
      confirm.onFalse();
    }
  };

  const isDeleting = busyState?.isBusy(`delete-${report.uri}`);
  const canDelete = onDeleteReport && isUserFolderReport(report.uri);
  const canEditReportContext = canEditContext || isUserFolderReport(report.uri);
  const description = report.description || '—';

  return (
    <>
      <TableRow
        hover
        sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
      >
        <TableCell
          align="center"
          sx={{
            width: { xs: 44, md: 100 },
            px: { xs: 2, md: 2 },
          }}
        >
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleToggleFavorite(report.uri);
            }}
            disabled={loadingFavorites.has(report.uri)}
            sx={{
              p: 0,
              transition: 'all 0.3s ease-in-out',
              animation: animatingFavorites.has(report.uri)
                ? `${starPulse} 0.3s ease-in-out`
                : 'none',
              '&:hover': {
                transform: loadingFavorites.has(report.uri) ? 'none' : 'scale(1.2)',
              },
              '&:disabled': {
                opacity: 1,
              },
            }}
          >
            {loadingFavorites.has(report.uri) ? (
              <CircularProgress
                size={20}
                sx={{
                  color: favorites.has(report.uri) ? 'warning.main' : 'text.disabled',
                }}
              />
            ) : (
              <Iconify
                icon={favorites.has(report.uri) ? 'eva:star-fill' : 'eva:star-outline'}
                width={20}
                sx={{ color: favorites.has(report.uri) ? 'warning.main' : 'text.disabled' }}
              />
            )}
          </IconButton>
        </TableCell>
        <TableCell
          onClick={handleReportClick}
          sx={{
            minWidth: 0,
            maxWidth: compact ? undefined : 300,
            color: 'primary.main',
            fontWeight: 500,
            cursor: 'pointer',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0 }}>
              <Box
                component="span"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  minWidth: 0,
                  flex: '1 1 auto',
                }}
              >
                {report.label}
                <ReportOpenInNewTabButton
                  uri={report.uri}
                  resourceType={report.resourceType || 'reportUnit'}
                />
              </Box>
            </Box>
            {compact && report.description && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'block',
                  mt: 0.25,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {report.description}
              </Typography>
            )}
          </Box>
        </TableCell>
        {!compact && (
          <TableCell sx={{ width: '60%', maxWidth: 0 }}>
            <Tooltip
              title={report.description || ''}
              placement="top"
              arrow
              disableHoverListener={!report.description}
            >
              <Box
                component="span"
                sx={{
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  color: 'text.secondary',
                  fontSize: '0.9rem',
                }}
              >
                {description}
              </Box>
            </Tooltip>
          </TableCell>
        )}
        {showActionsColumn && (
          <TableCell
            align="center"
            sx={{
              width: { xs: 44, md: 72 },
              px: { xs: 0.5, md: 2 },
              whiteSpace: 'nowrap',
            }}
          >
            {canEditReportContext && (
              <Tooltip title="Edit report context" placement="top" arrow>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    contextDialog.onTrue();
                  }}
                >
                  <Iconify icon="solar:settings-bold" width={20} />
                </IconButton>
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title="Delete" placement="top" arrow>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    confirm.onTrue();
                  }}
                  disabled={isDeleting}
                  sx={{ color: 'error.main' }}
                >
                  <Iconify icon="solar:trash-bin-trash-bold" width={20} />
                </IconButton>
              </Tooltip>
            )}
          </TableCell>
        )}
      </TableRow>

      {canDelete && (
        <ConfirmDialog
          open={confirm.value}
          onClose={confirm.onFalse}
          title="Delete report"
          content={`Are you sure you want to delete "${report.label}"? This cannot be undone.`}
          action={
            <Button
              variant="contained"
              color="error"
              onClick={handleDelete}
              loading={isDeleting}
            >
              Delete
            </Button>
          }
        />
      )}

      {canEditReportContext && (
        <ReportContextDialog
          open={contextDialog.value}
          onClose={contextDialog.onFalse}
          report={report}
        />
      )}
    </>
  );
}
